import React, { useCallback, useEffect, useState } from "react";
import {
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CalendarIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../../config/configApi";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

// Importar configurações de tema
import {
  getCurrentTheme,
  getRandomMotivationalMessage,
} from "../../config/themeConfig";

// Registrando os componentes do Chart.js
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

interface Patient {
  id: string;
  nomePaciente: string;
  classe: string;
  ultimaVisita: string;
  proximaVisita: string;
  statusVisitaPrograma: number;
  cep: string;
  regiao: string;
  bairro: string;
  localidade: string;
  fisio: string;
  fono: string;
  psico: string;
  nutri: string;
  curativo: boolean;
  alimentacaoEnteral: string;
  regimeCoparticipacao: string;
  lemedh: string[];
  observacao: string;
  orgaoVacinacao: string;
  status?: string;
  img: { imagem: string }[] | string;
  avaliacoes?: Array<{
    classificacaoRisco?: number;
    classificacaoDescricao?: string;
    dataAvaliacao?: string;
    nomeProfissionalAvaliacao?: string | null;
  }>;
}

interface Imagem {
  imagem_id: number;
  paciente_id: number;
  imagem: string;
}

interface Paciente {
  id: number;
  nome: string;
  text: string;
  signaturePacient: string;
  signature: string;
  date: string;
  data_registro: string;
  imagens: Imagem[];
}

const Dashboard: React.FC = () => {
  const [search,] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [cepOrigem, setCepOrigem] = useState("");
  const [isFirstModalOpen, setIsFirstModalOpen] = useState(false);
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [patientsWithVisita, setPatientsWithVisita] = useState<any[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [currentPatientName, setCurrentPatientName] = useState("");
  const [isFotosCurativoVisible, setFotosCurativoVisible] = useState(false);
  const [pacientes, ] = useState<Paciente[]>([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [modalAberto, setModalAberto] = useState(false);
  const [imagemExpandida, setImagemExpandida] = useState<string | null>(null);
  const [pacienteSelecionado, setPacienteSelecionado] =
    useState<Paciente | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Estado para tema atual e mensagem motivacional
  const [currentTheme, setCurrentTheme] = useState(getCurrentTheme());
  const [motivationalMessage, setMotivationalMessage] = useState(
    getRandomMotivationalMessage(currentTheme)
  );

  const itensPorPagina = 3;

  // Estados para paginação de Pacientes com Documentos
  const [documentosPaginaAtual, setDocumentosPaginaAtual] = useState(1);
  const itensPorPaginaDocumentos = 3;

  useEffect(() => {
    const handleBeforeUnload = () => {
      Cookies.remove("Frontend");
      Cookies.remove("UserRole");
      Cookies.remove("userIdEspecialide");
      Cookies.remove("Username");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    // Atualizar tema e mensagem motivacional periodicamente
    const interval = setInterval(() => {
      const newTheme = getCurrentTheme();
      setCurrentTheme(newTheme);
      setMotivationalMessage(getRandomMotivationalMessage(newTheme));
    }, 3600000); // Atualiza a cada hora

    return () => clearInterval(interval);
  }, []);

  const handleImageClick = (imgSrc: string) => {
    setSelectedImage(imgSrc);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const getPreviousMonth = () => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() - 1);
    return (currentDate.getMonth() + 1).toString().padStart(2, "0");
  };

  const getCurrentMonth = () => {
    const currentDate = new Date();
    return (currentDate.getMonth() + 1).toString().padStart(2, "0");
  };

const filterPatientsByMonth = useCallback((month: string) => {
  return patients.filter((patient) => {
    if (!patient || !patient.proximaVisita) return false;
    try {
      const visitDate = new Date(patient.proximaVisita);
      if (isNaN(visitDate.getTime())) return false;
      const visitMonth = (visitDate.getMonth() + 1)
        .toString()
        .padStart(2, "0");
      return visitMonth === month && patient.statusVisitaPrograma === 0;
    } catch {
      return false;
    }
  });
}, [patients]); // Add patients as dependency

  useEffect(() => {
    if (selectedMonth === getPreviousMonth()) {
      setPatientsWithVisita(filterPatientsByMonth(getPreviousMonth()));
    } else if (selectedMonth === getCurrentMonth()) {
      setPatientsWithVisita(filterPatientsByMonth(getCurrentMonth()));
    }
  }, [selectedMonth, patients, filterPatientsByMonth]);

  const isToday = (date: string) => {
    if (!date) return false;
    try {
      const today = new Date();
      const visitDate = new Date(date);
      return today.toDateString() === visitDate.toDateString();
    } catch {
      return false;
    }
  };

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("Frontend");
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }

        const response = await api.get("/sisclinic/sad/pacientes", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setPatients(response.data || []);
      } catch (error) {
        console.error("Erro ao buscar pacientes:", error);

        if (error && typeof error === "object" && "response" in error) {
          const err = error as { response?: { status?: number } };

          if (err.response?.status === 403) {
            Swal.fire({
              icon: "warning",
              title: "Acesso Negado",
              text: "Você não tem permissão para acessar esta lista de pacientes.",
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Erro ao carregar pacientes",
              text: "Houve um erro ao tentar buscar os pacientes. Tente novamente mais tarde.",
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro inesperado",
            text: "Ocorreu um erro inesperado. Tente novamente.",
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  const sortedPatients = [...(patients || [])].sort((a, b) => {
    const cepA = a?.cep || "";
    const cepB = b?.cep || "";
    return cepA.localeCompare(cepB);
  });

  // Função para verificar se um paciente tem documentos/curativos - AJUSTADA para booleano
  const pacienteTemDocumentos = (patient: Patient) => {
    if (!patient) return false;

    // Verificar se curativo é verdadeiro (booleano true)
    if (!patient.curativo) {
      return false;
    }

    // Verifica se há imagens/documentos
    if (Array.isArray(patient.img) && patient.img.length > 0) {
      return true;
    } else if (typeof patient.img === "string" && patient.img.trim() !== "") {
      return true;
    }

    return false;
  };

  // Filtra apenas pacientes com documentos
  const pacientesComDocumentos = (sortedPatients || []).filter(
    (patient) => patient && pacienteTemDocumentos(patient)
  );

  // Cálculos para paginação de documentos
  const totalPaginasDocumentos = Math.ceil(
    pacientesComDocumentos.length / itensPorPaginaDocumentos
  );
  const inicioDocumentos =
    (documentosPaginaAtual - 1) * itensPorPaginaDocumentos;
  const documentosPaginados = pacientesComDocumentos.slice(
    inicioDocumentos,
    inicioDocumentos + itensPorPaginaDocumentos
  );

  const assistanceCount = (patients || []).reduce(
    (acc, patient) => {
      if (patient?.fisio) acc.fisio++;
      if (patient?.fono) acc.fono++;
      if (patient?.psico) acc.psico++;
      if (patient?.nutri) acc.nutri++;
      return acc;
    },
    { fisio: 0, fono: 0, psico: 0, nutri: 0 }
  );

  const data = {
    labels: ["Fisioterapia", "Fonoaudiologia", "Psicologia", "Nutrição"],
    datasets: [
      {
        label: "Assistências Prestadas",
        data: [
          assistanceCount.fisio,
          assistanceCount.fono,
          assistanceCount.psico,
          assistanceCount.nutri,
        ],
        backgroundColor: ["#4CAF50", "#2196F3", "#FFC107", "#FF5722"],
        borderColor: ["#388E3C", "#1976D2", "#FFA000", "#D84315"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw} assistência(s)`,
        },
      },
    },
  };

  const countByRegion = (patients: Patient[]) => {
    const regionCount: Record<string, number> = {};

    (patients || []).forEach((patient) => {
      const { regiao } = patient || {};
      if (regiao) {
        regionCount[regiao] = (regionCount[regiao] || 0) + 1;
      }
    });

    return regionCount;
  };

  const countByLocation = (patients: Patient[]) => {
    const locationCount: Record<string, number> = {};

    (patients || []).forEach((patient) => {
      const { localidade } = patient || {};
      if (localidade) {
        locationCount[localidade] = (locationCount[localidade] || 0) + 1;
      }
    });

    return locationCount;
  };

  const regions = countByRegion(patients);
  const locations = countByLocation(patients);

  const combineRegionAndLocationData = (
    regionData: Record<string, number>,
    locationData: Record<string, number>
  ) => {
    const combinedData: Record<string, number> = {};

    Object.entries(regionData).forEach(([key, value]) => {
      combinedData[key] = (combinedData[key] || 0) + value;
    });

    Object.entries(locationData).forEach(([key, value]) => {
      combinedData[key] = (combinedData[key] || 0) + value;
    });

    return combinedData;
  };

  const combinedData = combineRegionAndLocationData(regions, locations);

  const combinedColors = {
    Nordeste: "#f44336",
    Sudeste: "#9c27b0",
    Leste: "#3f51b5",
    Oeste: "#00bcd4",
    "João Pessoa": "#00bcd4",
    Cabedelo: "#3f51b5",
  };

  const pieDataCombined = {
    labels: Object.keys(combinedData),
    datasets: [
      {
        label: "Pacientes por Região e Localidade",
        data: Object.values(combinedData),
        backgroundColor: Object.keys(combinedData).map((key) => {
          const regionKey = key.trim();
          return (
            combinedColors[regionKey as keyof typeof combinedColors] ||
            "#888888"
          );
        }),
      },
    ],
  };

  const gerarDadosPorMesAno = (campo: "ultimaVisita" | "proximaVisita") => {
    const meses: { [key: string]: number } = {};
    const anoAtual = new Date().getFullYear();

    (patients || []).forEach((patient) => {
      const dataString = patient?.[campo];
      if (dataString) {
        try {
          const data = new Date(dataString);
          if (!isNaN(data.getTime())) {
            const mesAnoChave = `${data.getMonth() + 1}/${data.getFullYear()}`;

            if (data.getFullYear() >= anoAtual - 1) {
              meses[mesAnoChave] = (meses[mesAnoChave] || 0) + 1;
            }
          }
        } catch {
          // Ignora datas inválidas
        }
      }
    });

    return meses;
  };

  const mesesUltimaVisita = gerarDadosPorMesAno("ultimaVisita");
  const mesesproximaVisita = gerarDadosPorMesAno("proximaVisita");

  const gerarLabels = (mesesData: { [key: string]: number }) => {
    return Object.keys(mesesData)
      .map((key) => {
        const [mes, ano] = key.split("/").map(Number);
        return { mes, ano, label: new Date(ano, mes - 1, 1) };
      })
      .sort((a, b) => a.ano - b.ano || a.mes - b.mes)
      .map(({ label }) => {
        return label.toLocaleString("pt-BR", {
          month: "long",
          year: "numeric",
        });
      });
  };

  const monthlyCountsUltimaVisita = Object.values(mesesUltimaVisita);
  const monthlyCountsproximaVisita = Object.values(mesesproximaVisita);

  const gerarCores = (labels: string[]) => {
    return labels.map(() => {
      const randomColor = `hsl(${Math.floor(Math.random() * 360)}, 70%, 60%)`;
      return randomColor;
    });
  };

  const barColorsUltimaVisita = gerarCores(Object.keys(mesesUltimaVisita));
  const barColorsproximaVisita = gerarCores(Object.keys(mesesproximaVisita));

  const monthlyDataUltimaVisita = {
    labels: gerarLabels(mesesUltimaVisita),
    datasets: [
      {
        label: "Quantidade de Pacientes - Última Visita",
        data: monthlyCountsUltimaVisita,
        backgroundColor: barColorsUltimaVisita,
        borderColor: barColorsUltimaVisita.map((color) =>
          color.replace("hsl", "rgba").replace(")", ", 0.7)")
        ),
        borderWidth: 1,
      },
    ],
  };

  const monthlyDataproximaVisita = {
    labels: gerarLabels(mesesproximaVisita),
    datasets: [
      {
        label: "Quantidade de Pacientes - Visita Programada",
        data: monthlyCountsproximaVisita,
        backgroundColor: barColorsproximaVisita,
        borderColor: barColorsproximaVisita.map((color) =>
          color.replace("hsl", "rgba").replace(")", ", 0.7)")
        ),
        borderWidth: 1,
      },
    ],
  };

  const monthlyOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `Pacientes: ${context.raw}`;
          },
        },
      },
    },
  };

  // CORREÇÃO DO ERRO: Verificação de segurança para filteredPatients
  const filteredPatients = (patients || []).filter((patient) => {
    // Verifica se patient e patient.nomePaciente existem
    if (!patient || !patient.nomePaciente) return false;

    // Converte para minúsculas e verifica se contém a busca
    const nome = patient.nomePaciente.toString().toLowerCase();
    const searchTerm = search.toLowerCase();
    return nome.includes(searchTerm);
  });

  useEffect(() => {
    const patientsWithproximaVisita = (sortedPatients || []).filter((p) => {
      // CORREÇÃO DO ERRO: Verificação de segurança para proximaVisita
      if (!p || !p.proximaVisita || !p.nomePaciente) return false;

      try {
        const visitaData = p.proximaVisita.split("T")[0];
        return visitaData === getCurrentDate();
      } catch {
        return false;
      }
    });

    if (patientsWithproximaVisita.length > 0 && isAlertVisible) {
      setPatientsWithVisita(patientsWithproximaVisita);
      setIsFirstModalOpen(true);
      setIsAlertVisible(false);
    }
  }, [sortedPatients, isAlertVisible]);


  const abrirModal = (paciente: Paciente) => {
    setPacienteSelecionado(paciente);
    setModalAberto(true);
  };

  const expandirImagem = (imagem: string) => {
    setImagemExpandida(imagem);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setPacienteSelecionado(null);
  };

  const openFotosCurativo = (patientName: string) => {
    setCurrentPatientName(patientName);
    setFotosCurativoVisible(true);
  };

  const closeFotosCurativo = () => setFotosCurativoVisible(false);

  // Skeleton Loaders
  const CardSkeleton = () => (
    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 animate-pulse">
      <div className="flex flex-col items-center">
        <div className="bg-gray-200 p-3 rounded-full mb-4 h-14 w-14"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="w-24 h-24 bg-gray-200 rounded-full mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  );

  const ChartSkeleton = () => (
    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-1/2 mb-6"></div>
      <div className="h-64 bg-gray-200 rounded"></div>
    </div>
  );

  // Filtra pacientes com curativos (booleano true) com verificação de segurança
  const pacientesComCurativos = (patients || []).filter(
    (patient) => patient && patient.curativo === true
  );

  const totalPaginasCurativos = Math.ceil(
    pacientesComCurativos.length / itensPorPagina
  );
  const inicioCurativos = (paginaAtual - 1) * itensPorPagina;
  const pacientesCurativosPaginados = pacientesComCurativos.slice(
    inicioCurativos,
    inicioCurativos + itensPorPagina
  );

  return (
    <div
      className={`p-4 md:p-6 ${currentTheme.colors.background} min-h-screen`}
    >
      {/* Header com tema */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 md:mb-8">
        <motion.div
          className="flex items-center mb-4 md:mb-0"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <img
            src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
            alt="Logo AFRAFEP"
            className="w-40 md:w-60 h-auto"
          />
          <div className="ml-4">
            <div
              className={`text-2xl md:text-3xl font-bold ${currentTheme.colors.text}`}
            >
              {currentTheme.decorations.emoji} {currentTheme.messages.title}
            </div>
            <div
              className={`text-sm md:text-base ${currentTheme.colors.text} opacity-90`}
            >
              {motivationalMessage}
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <Link to="/Sad_Formulario" className="w-full sm:w-auto">
            <motion.button
              className={`px-4 py-2 md:px-6 md:py-3 ${currentTheme.colors.button} text-white font-semibold rounded-lg transition shadow-md hover:shadow-lg flex items-center justify-center w-full`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
              <span className="text-sm md:text-base">Gerenciar Pacientes</span>
            </motion.button>
          </Link>

          <Link to="/Home" className="w-full sm:w-auto">
            <motion.button
              className="px-4 py-2 md:px-6 md:py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition shadow-md hover:shadow-lg flex items-center justify-center w-full mt-2 sm:mt-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              <span className="text-sm md:text-base">Voltar para Início</span>
            </motion.button>
          </Link>
        </div>
      </div>

      <motion.header
        className="mb-6 md:mb-8 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">
          Painel de Assistência
        </h1>
        <p className="text-sm md:text-base text-gray-800">
          {currentTheme.messages.subtitle}
        </p>
      </motion.header>

      {/* Grid Principal */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Card de Total de Pacientes */}
          <motion.div
            className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-blue-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <div className="flex flex-col items-center">
              <div className="bg-blue-100 p-2 md:p-3 rounded-full mb-3 md:mb-4">
                <UserGroupIcon className="h-6 md:h-8 w-6 md:w-8 text-blue-600" />
              </div>
              <h2 className="text-lg md:text-xl font-semibold text-gray-700 mb-3 md:mb-4">
                Total de Pacientes
              </h2>
              <div className="w-20 h-20 md:w-24 md:h-24 bg-blue-100 rounded-full flex items-center justify-center mb-3 md:mb-4">
                <p className="text-2xl md:text-3xl font-bold text-blue-600">
                  {patients.length}
                </p>
              </div>
              <p className="text-xs md:text-sm text-gray-500">
                Cadastrados no sistema
              </p>
            </div>
          </motion.div>

          {/* Card de Classificações - CORRIGIDO para usar avaliacoes.classificacaoDescricao */}
          <motion.div
            className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-green-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h2 className="text-lg md:text-xl font-semibold text-gray-700 mb-3 md:mb-4">
              Classificações de Pacientes
            </h2>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="p-3 md:p-4 bg-green-50 rounded-lg md:rounded-xl text-center border border-green-200">
                <h3 className="text-xs md:text-sm font-semibold text-green-600 mb-1 md:mb-2">
                  BAIXA
                </h3>
                <p className="text-xl md:text-2xl font-bold text-green-600">
                  {
                    (patients || []).filter((p) =>
                      p?.avaliacoes?.some(
                        (a) => a.classificacaoDescricao === "BAIXA"
                      )
                    ).length
                  }
                </p>
              </div>
              <div className="p-3 md:p-4 bg-blue-50 rounded-lg md:rounded-xl text-center border border-blue-200">
                <h3 className="text-xs md:text-sm font-semibold text-blue-600 mb-1 md:mb-2">
                  REDE
                </h3>
                <p className="text-xl md:text-2xl font-bold text-blue-600">
                  {
                    (patients || []).filter((p) =>
                      p?.avaliacoes?.some(
                        (a) => a.classificacaoDescricao === "REDE"
                      )
                    ).length
                  }
                </p>
              </div>
              <div className="p-3 md:p-4 bg-yellow-50 rounded-lg md:rounded-xl text-center border border-yellow-200">
                <h3 className="text-xs md:text-sm font-semibold text-yellow-600 mb-1 md:mb-2">
                  MÉDIA
                </h3>
                <p className="text-xl md:text-2xl font-bold text-yellow-600">
                  {
                    (patients || []).filter((p) =>
                      p?.avaliacoes?.some(
                        (a) => a.classificacaoDescricao === "MÉDIA"
                      )
                    ).length
                  }
                </p>
              </div>
              <div className="p-3 md:p-4 bg-orange-50 rounded-lg md:rounded-xl text-center border border-orange-200">
                <h3 className="text-xs md:text-sm font-semibold text-orange-600 mb-1 md:mb-2">
                  ALTA
                </h3>
                <p className="text-xl md:text-2xl font-bold text-orange-600">
                  {
                    (patients || []).filter((p) =>
                      p?.avaliacoes?.some(
                        (a) => a.classificacaoDescricao === "ALTA"
                      )
                    ).length
                  }
                </p>
              </div>
              <div className="p-3 md:p-4 bg-gray-50 rounded-lg md:rounded-xl text-center border border-gray-200 col-span-2">
                <h3 className="text-xs font-semibold text-gray-700 mb-1 md:mb-2">
                  SEM INDICAÇÃO
                </h3>
                <p className="text-xl md:text-2xl font-bold text-gray-800">
                  {
                    (patients || []).filter((p) =>
                      p?.avaliacoes?.some(
                        (a) =>
                          a.classificacaoDescricao ===
                          "SEM INDICACAO DOMICILIAR"
                      )
                    ).length
                  }
                </p>
              </div>
            </div>
          </motion.div>

          {/* Card de Vacinação */}
          <motion.div
            className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-yellow-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <h2 className="text-lg md:text-xl font-semibold text-gray-700 mb-3 md:mb-4">
              Vacinação
            </h2>
            <div className="grid grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="bg-yellow-50 p-3 md:p-4 rounded-lg md:rounded-xl text-center border border-yellow-200">
                <p className="text-xs md:text-sm font-semibold text-yellow-700 mb-1">
                  SAD
                </p>
                <p className="text-xl md:text-2xl font-bold text-yellow-700">
                  {
                    (patients || []).filter((p) => p?.orgaoVacinacao === "SAD")
                      .length
                  }
                </p>
              </div>
              <div className="bg-blue-50 p-3 md:p-4 rounded-lg md:rounded-xl text-center border border-blue-200">
                <p className="text-xs md:text-sm font-semibold text-blue-700 mb-1">
                  SEC SAÚDE
                </p>
                <p className="text-xl md:text-2xl font-bold text-blue-700">
                  {
                    (patients || []).filter(
                      (p) => p?.orgaoVacinacao === "SEC_DE_SAUDE"
                    ).length
                  }
                </p>
              </div>
            </div>

            <div className="border-t pt-3 md:pt-4">
              <h2 className="text-lg md:text-xl font-semibold text-gray-700 mb-3 md:mb-4">
                Alimentação Enteral
              </h2>

              {(patients || []).filter(
                (p) => p?.alimentacaoEnteral && p.alimentacaoEnteral !== "vazio"
              ).length > 0 ? (
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  <div className="bg-blue-50 p-2 md:p-3 rounded-lg text-center border border-blue-200">
                    <p className="text-xs font-semibold text-blue-700 mb-1">
                      SNE
                    </p>
                    <p className="text-lg md:text-xl font-bold text-blue-700">
                      {
                        (patients || []).filter(
                          (p) => p?.alimentacaoEnteral === "SNE"
                        ).length
                      }
                    </p>
                  </div>
                  <div className="bg-green-50 p-2 md:p-3 rounded-lg text-center border border-green-200">
                    <p className="text-xs font-semibold text-green-700 mb-1">
                      GTT
                    </p>
                    <p className="text-lg md:text-xl font-bold text-green-700">
                      {
                        (patients || []).filter(
                          (p) => p?.alimentacaoEnteral === "GTT"
                        ).length
                      }
                    </p>
                  </div>
                  <div className="bg-purple-50 p-2 md:p-3 rounded-lg text-center border border-purple-200">
                    <p className="text-xs font-semibold text-purple-700 mb-1">
                      SOG
                    </p>
                    <p className="text-lg md:text-xl font-bold text-purple-700">
                      {
                        (patients || []).filter(
                          (p) => p?.alimentacaoEnteral === "SOG"
                        ).length
                      }
                    </p>
                  </div>
                  <div className="bg-amber-50 p-2 md:p-3 rounded-lg text-center border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700 mb-1">
                      OUTROS
                    </p>
                    <p className="text-lg md:text-xl font-bold text-amber-700">
                      {
                        (patients || []).filter(
                          (p) => p?.alimentacaoEnteral === "X"
                        ).length
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-3 md:py-4 text-sm md:text-base">
                  Nenhum paciente com alimentação enteral
                </p>
              )}

              <div className="mt-3 md:mt-4 text-center bg-gray-50 py-2 rounded-lg">
                <p className="text-xs md:text-sm text-gray-900">
                  Total:{" "}
                  {
                    (patients || []).filter(
                      (p) =>
                        p?.alimentacaoEnteral &&
                        p.alimentacaoEnteral !== "vazio"
                    ).length
                  }
                </p>
              </div>
            </div>
          </motion.div>

          {/* Card de Visitas Programadas */}
          <motion.div
            className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-pink-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <div className="flex items-center mb-3 md:mb-4">
              <div className="bg-pink-100 p-2 rounded-lg mr-3">
                <CalendarIcon className="h-5 md:h-6 w-5 md:w-6 text-pink-600" />
              </div>
              <h2 className="text-lg md:text-xl font-semibold text-gray-700">
                Visitas Programadas
              </h2>
            </div>

            {/* Alerta de visitas para hoje */}
            {patientsWithVisita.some((patient) =>
              isToday(patient?.proximaVisita)
            ) && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-2 md:p-3 rounded-lg mb-3 md:mb-4 flex items-center text-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 md:h-5 md:w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">
                  Visita programada para hoje!
                </span>
              </div>
            )}

            <div className="mb-3 md:mb-4">
              <label
                htmlFor="monthSelect"
                className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2"
              >
                Selecione o mês
              </label>
              <select
                id="monthSelect"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
              >
                <option value="">Selecione um mês</option>
                <option value={getPreviousMonth()}>Mês Passado</option>
                <option value={getCurrentMonth()}>Mês Atual</option>
              </select>
            </div>

            <div className="text-center mb-3 md:mb-4">
              <p className="text-2xl md:text-3xl font-bold text-pink-600">
                {patientsWithVisita.length}
              </p>
              <p className="text-xs md:text-sm text-gray-500">
                visitas programadas
              </p>
            </div>

            {patientsWithVisita.length > 0 && (
              <motion.button
                onClick={() => setIsSecondModalOpen(true)}
                className={`w-full ${currentTheme.colors.button} text-white py-2 rounded-lg font-medium transition flex items-center justify-center text-sm md:text-base`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MapPinIcon className="h-4 w-4 md:h-4 md:w-4 mr-1 md:mr-2" />
                Ver Detalhes e Rotas
              </motion.button>
            )}
          </motion.div>

          {/* Card de Pacientes com Curativos */}
          <motion.div
            className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-purple-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 md:mb-4 gap-2">
              <div className="flex items-center">
                <div className="bg-purple-100 p-2 rounded-lg mr-3">
                  <ClipboardDocumentListIcon className="h-5 md:h-6 w-5 md:w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-gray-700">
                    Pacientes com Curativos
                  </h2>
                  <p className="text-xs md:text-sm text-gray-500">
                    {pacientesComCurativos.length} paciente
                    {pacientesComCurativos.length !== 1 ? "s" : ""} com
                    curativos
                  </p>
                </div>
              </div>

              {totalPaginasCurativos > 1 && (
                <div className="flex items-center space-x-2 self-end md:self-auto">
                  <button
                    onClick={() =>
                      setPaginaAtual((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={paginaAtual === 1}
                    className="p-1 md:p-2 bg-gray-200 rounded-full disabled:opacity-50 hover:bg-gray-300 transition"
                  >
                    <ChevronLeftIcon className="h-3 w-3 md:h-4 md:w-4" />
                  </button>
                  <span className="text-xs md:text-sm text-gray-600">
                    {paginaAtual} / {totalPaginasCurativos}
                  </span>
                  <button
                    onClick={() =>
                      setPaginaAtual((prev) =>
                        Math.min(prev + 1, totalPaginasCurativos)
                      )
                    }
                    disabled={paginaAtual === totalPaginasCurativos}
                    className="p-1 md:p-2 bg-gray-200 rounded-full disabled:opacity-50 hover:bg-gray-300 transition"
                  >
                    <ChevronRightIcon className="h-3 w-3 md:h-4 md:w-4" />
                  </button>
                </div>
              )}
            </div>

            {pacientesComCurativos.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 md:gap-4 mb-3 md:mb-4">
                {pacientesCurativosPaginados.map(
                  (patient: Patient, index: number) => {
                    if (!patient) return null;

                    // Encontrar paciente correspondente na lista de pacientes com imagens
                    const pacienteComImagens = (pacientes || []).find(
                      (p) =>
                        p &&
                        patient &&
                        (p.nome === patient.nomePaciente ||
                          p.id.toString() === patient.id)
                    );

                    return (
                      <motion.div
                        key={patient?.id || index}
                        className="bg-white rounded-lg md:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 cursor-pointer"
                        whileHover={{ y: -5 }}
                        onClick={() => {
                          if (pacienteComImagens) {
                            abrirModal(pacienteComImagens);
                          }
                        }}
                      >
                        <div className="p-3 md:p-4 flex justify-between items-center">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-800 text-base md:text-lg truncate">
                              {patient?.nomePaciente || "Nome não disponível"}
                            </h3>

                            <div className="flex items-center mt-2">
                              <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                                <ClipboardDocumentListIcon className="h-3 w-3 mr-1" />
                                {pacienteComImagens?.imagens?.length || 0}{" "}
                                curativo
                                {pacienteComImagens?.imagens?.length !== 1
                                  ? "s"
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <div className="bg-purple-100 p-1 md:p-2 rounded-full">
                            <EyeIcon className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                          </div>
                        </div>
                      </motion.div>
                    );
                  }
                )}
              </div>
            ) : (
              <div className="text-center py-6 md:py-8 bg-gray-50 rounded-xl">
                <ClipboardDocumentListIcon className="h-8 w-8 md:h-12 md:w-12 mx-auto text-gray-400 mb-2 md:mb-3" />
                <p className="text-sm md:text-base text-gray-500">
                  Nenhum paciente com curativos encontrado.
                </p>
                <p className="text-xs md:text-sm text-gray-400 mt-1">
                  Os pacientes com curativos aparecerão aqui automaticamente.
                </p>
              </div>
            )}

            {/* Paginação para Pacientes com Curativos */}
            {pacientesComCurativos.length > 0 && totalPaginasCurativos > 1 && (
              <div className="flex justify-between items-center border-t pt-3 md:pt-4">
                <button
                  onClick={() =>
                    setPaginaAtual((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={paginaAtual === 1}
                  className="flex items-center px-2 py-1 md:px-3 md:py-1 bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm"
                >
                  <ChevronLeftIcon className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                  Anterior
                </button>

                <span className="text-xs md:text-sm text-gray-600">
                  Página {paginaAtual} de {totalPaginasCurativos}
                </span>

                <button
                  onClick={() =>
                    setPaginaAtual((prev) =>
                      Math.min(prev + 1, totalPaginasCurativos)
                    )
                  }
                  disabled={paginaAtual === totalPaginasCurativos}
                  className="flex items-center px-2 py-1 md:px-3 md:py-1 bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm"
                >
                  Próximo
                  <ChevronRightIcon className="h-3 w-3 md:h-4 md:w-4 ml-1" />
                </button>
              </div>
            )}
          </motion.div>

          {/* Card de Pacientes com Documentos */}
          <motion.div
            className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-indigo-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 md:mb-4 gap-2">
              <div className="flex items-center">
                <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                  <DocumentTextIcon className="h-5 md:h-6 w-5 md:w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-gray-700">
                    Pacientes com Documentos
                  </h2>
                  <p className="text-xs md:text-sm text-gray-500">
                    {pacientesComDocumentos.length} paciente
                    {pacientesComDocumentos.length !== 1 ? "s" : ""} com
                    documentação
                  </p>
                </div>
              </div>

              {totalPaginasDocumentos > 1 && (
                <div className="flex items-center space-x-2 self-end md:self-auto">
                  <button
                    onClick={() =>
                      setDocumentosPaginaAtual((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={documentosPaginaAtual === 1}
                    className="p-1 md:p-2 bg-gray-200 rounded-full disabled:opacity-50 hover:bg-gray-300 transition"
                  >
                    <ChevronLeftIcon className="h-3 w-3 md:h-4 md:w-4" />
                  </button>
                  <span className="text-xs md:text-sm text-gray-600">
                    {documentosPaginaAtual} / {totalPaginasDocumentos}
                  </span>
                  <button
                    onClick={() =>
                      setDocumentosPaginaAtual((prev) =>
                        Math.min(prev + 1, totalPaginasDocumentos)
                      )
                    }
                    disabled={documentosPaginaAtual === totalPaginasDocumentos}
                    className="p-1 md:p-2 bg-gray-200 rounded-full disabled:opacity-50 hover:bg-gray-300 transition"
                  >
                    <ChevronRightIcon className="h-3 w-3 md:h-4 md:w-4" />
                  </button>
                </div>
              )}
            </div>

            {pacientesComDocumentos.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 md:gap-4 mb-3 md:mb-4">
                {documentosPaginados.map((patient, index) => {
                  if (!patient) return null;

                  // Processar imagens do paciente
                  let imagens: string[] = [];

                  if (Array.isArray(patient.img)) {
                    imagens = patient.img.map((imgObj) => imgObj.imagem);
                  } else if (typeof patient.img === "string") {
                    const cleanedString = patient.img
                      .replace(/^\["|"\]$/g, "")
                      .replace(/"/g, "");
                    imagens = cleanedString
                      .split(",")
                      .map((img) => img.trim())
                      .filter((img) => img !== "");
                  }

                  return (
                    <motion.div
                      key={index}
                      className="bg-white rounded-lg md:rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 cursor-pointer"
                      whileHover={{ y: -5 }}
                      onClick={() =>
                        openFotosCurativo(patient.nomePaciente || "")
                      }
                    >
                      <div className="p-3 md:p-4 flex justify-between items-center">
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-800 text-base md:text-lg truncate">
                            {patient.nomePaciente || "Nome não disponível"}
                          </h3>
                          <p className="text-xs md:text-sm text-gray-500 mt-1">
                            {patient.bairro || ""}
                          </p>
                          <div className="flex items-center mt-2">
                            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                              <DocumentTextIcon className="h-3 w-3 mr-1" />
                              {imagens.length} documento
                              {imagens.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                        <div className="bg-blue-100 p-1 md:p-2 rounded-full">
                          <EyeIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 md:py-8 bg-gray-50 rounded-xl">
                <DocumentTextIcon className="h-8 w-8 md:h-12 md:w-12 mx-auto text-gray-400 mb-2 md:mb-3" />
                <p className="text-sm md:text-base text-gray-500">
                  Nenhum paciente com documentos encontrado.
                </p>
                <p className="text-xs md:text-sm text-gray-400 mt-1">
                  Os pacientes com documentos aparecerão aqui automaticamente.
                </p>
              </div>
            )}

            {/* Paginação para Pacientes com Documentos */}
            {pacientesComDocumentos.length > 0 &&
              totalPaginasDocumentos > 1 && (
                <div className="flex justify-between items-center border-t pt-3 md:pt-4">
                  <button
                    onClick={() =>
                      setDocumentosPaginaAtual((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={documentosPaginaAtual === 1}
                    className="flex items-center px-2 py-1 md:px-3 md:py-1 bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm"
                  >
                    <ChevronLeftIcon className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                    Anterior
                  </button>

                  <span className="text-xs md:text-sm text-gray-600">
                    Página {documentosPaginaAtual} de {totalPaginasDocumentos}
                  </span>

                  <button
                    onClick={() =>
                      setDocumentosPaginaAtual((prev) =>
                        Math.min(prev + 1, totalPaginasDocumentos)
                      )
                    }
                    disabled={documentosPaginaAtual === totalPaginasDocumentos}
                    className="flex items-center px-2 py-1 md:px-3 md:py-1 bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm"
                  >
                    Próximo
                    <ChevronRightIcon className="h-3 w-3 md:h-4 md:w-4 ml-1" />
                  </button>
                </div>
              )}
          </motion.div>
        </div>
      )}

      {/* Seção de Gráficos */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      ) : (
        <motion.section
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          {/* Gráfico de Assistências */}
          <div className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-green-100">
            <h2 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4">
              Assistências Prestadas
            </h2>
            <div className="h-60 md:h-72">
              <Bar data={data} options={options} />
            </div>
          </div>

          {/* Gráfico de Última Visita */}
          <div className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-blue-100">
            <h2 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4">
              Visitas Realizadas
            </h2>
            <div className="h-60 md:h-72">
              <Bar data={monthlyDataUltimaVisita} options={monthlyOptions} />
            </div>
          </div>

          {/* Gráfico de Visita Programada */}
          <div className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-purple-100">
            <h2 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4">
              Visitas Programadas
            </h2>
            <div className="h-60 md:h-72">
              <Bar data={monthlyDataproximaVisita} options={monthlyOptions} />
            </div>
          </div>

          {/* Gráfico de Regiões */}
          <div className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-lg border border-red-100 md:col-span-2 lg:col-span-1">
            <h2 className="text-lg md:text-xl font-bold text-gray-700 mb-3 md:mb-4">
              Distribuição por Região
            </h2>
            <div className="h-60 md:h-72 flex items-center justify-center">
              <Pie
                data={pieDataCombined}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: {
                        boxWidth: 12,
                        padding: 15,
                        font: {
                          size: window.innerWidth < 768 ? 10 : 12,
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </motion.section>
      )}

      {/* Modal de Visitas Programadas para Hoje */}
      <AnimatePresence>
        {isFirstModalOpen &&
          patientsWithVisita.some((patient) =>
            isToday(patient?.proximaVisita)
          ) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <motion.div
                className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl w-full max-w-md"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="flex items-center mb-3 md:mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 md:h-6 md:w-6 text-yellow-500 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                    Visitas Programadas para Hoje
                  </h3>
                </div>

                <div className="mb-3 md:mb-4">
                  <ul className="space-y-2 max-h-60 overflow-y-auto">
                    {patientsWithVisita
                      .filter((patient) => isToday(patient?.proximaVisita))
                      .map((patient, index) => (
                        <li
                          key={index}
                          className="flex items-center p-2 bg-gray-50 rounded-lg"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 md:h-5 md:w-5 text-blue-500 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          <span className="text-sm md:text-base text-gray-700">
                            {patient?.nomePaciente || "Paciente sem nome"}
                          </span>
                          <span className="ml-auto text-xs text-gray-500">
                            {patient?.proximaVisita
                              ? new Date(
                                  patient.proximaVisita
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "Horário não disponível"}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>

                <button
                  onClick={() => setIsFirstModalOpen(false)}
                  className={`w-full ${currentTheme.colors.button} text-white py-2 rounded-lg font-medium transition text-sm md:text-base`}
                >
                  Entendi
                </button>
              </motion.div>
            </div>
          )}
      </AnimatePresence>

      {/* Modal de Detalhes de Visitas Programadas */}
      <AnimatePresence>
        {isSecondModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                  Detalhes das Visitas Programadas
                </h3>
                <button
                  onClick={() => setIsSecondModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 md:h-6 md:w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-3 md:mb-4">
                <label
                  htmlFor="cepOrigem"
                  className="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2"
                >
                  CEP de Origem (58013-260)
                </label>
                <input
                  id="cepOrigem"
                  type="text"
                  placeholder="Ex: 58015-100"
                  value={cepOrigem}
                  onChange={(e) => setCepOrigem(e.target.value)}
                  className="w-full p-2 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:text-base"
                />
              </div>

              <div className="mb-3 md:mb-4 flex-1 overflow-y-auto">
                <ul className="space-y-2 md:space-y-3">
                  {patientsWithVisita.map((patient, index) => {
                    if (!patient) return null;

                    return (
                      <li
                        key={index}
                        className="p-2 md:p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-1 md:gap-0">
                          <div>
                            <p className="font-medium text-gray-800 text-sm md:text-base">
                              {patient.nomePaciente || "Paciente sem nome"}
                            </p>
                            <p className="text-xs md:text-sm text-gray-900">
                              {patient.bairro || ""} - {patient.cep || ""}
                            </p>
                            <p className="text-xs text-gray-500">
                              {patient.proximaVisita
                                ? new Date(
                                    patient.proximaVisita
                                  ).toLocaleDateString()
                                : "Data não disponível"}{" "}
                              {patient.proximaVisita &&
                                `- ${new Date(
                                  patient.proximaVisita
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}`}
                            </p>
                          </div>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
                              cepOrigem
                            )}&destination=${encodeURIComponent(
                              patient.cep || ""
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-blue-600 hover:text-blue-800 text-xs md:text-sm font-medium mt-1 md:mt-0"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 md:h-4 md:w-4 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            Ver Rota
                          </a>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <button
                onClick={() => setIsSecondModalOpen(false)}
                className="w-full bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 transition text-sm md:text-base"
              >
                Fechar
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para exibir imagens do paciente */}
      <AnimatePresence>
        {modalAberto && pacienteSelecionado && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                  Curativos de {pacienteSelecionado.nome}
                </h3>
                <button
                  onClick={fecharModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 md:h-6 md:w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-3 md:mb-4 flex-1 overflow-y-auto">
                {(pacienteSelecionado.imagens || []).map(
                  (img: any, index: number) => (
                    <div key={index} className="relative group">
                      <img
                        src={`data:image/png;base64,${img.imagem}`}
                        alt={`Imagem ${index + 1}`}
                        className="w-full h-32 md:h-40 object-cover rounded-lg border border-gray-200 cursor-pointer transition group-hover:opacity-90"
                        onClick={() => expandirImagem(img.imagem)}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition rounded-lg flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 md:h-8 md:w-8 text-white opacity-0 group-hover:opacity-100 transition"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3-3H7"
                          />
                        </svg>
                      </div>
                    </div>
                  )
                )}
              </div>

              <button
                onClick={fecharModal}
                className="w-full bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 transition text-sm md:text-base"
              >
                Fechar
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para imagem expandida */}
      <AnimatePresence>
        {imagemExpandida && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={() => setImagemExpandida(null)}
          >
            <motion.div
              className="relative max-w-4xl max-h-full p-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <img
                src={`data:image/png;base64,${imagemExpandida}`}
                alt="Imagem expandida"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              <button
                onClick={() => setImagemExpandida(null)}
                className="absolute top-2 right-2 md:top-4 md:right-4 bg-red-600 text-white rounded-full p-1 md:p-2 hover:bg-red-700 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 md:h-6 md:w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para exibir documentos */}
      <AnimatePresence>
        {isFotosCurativoVisible && currentPatientName && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                  <DocumentTextIcon className="h-5 w-5 md:h-6 md:w-6 inline-block mr-2 text-indigo-600" />
                  Documentos - {currentPatientName}
                </h3>
                <button
                  onClick={closeFotosCurativo}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 md:h-6 md:w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4 flex-1 overflow-y-auto">
                {(filteredPatients || [])
                  .filter(
                    (patient) =>
                      patient?.nomePaciente === currentPatientName &&
                      pacienteTemDocumentos(patient)
                  )
                  .map((patient) => {
                    if (!patient) return null;

                    let imagens: string[] = [];

                    if (Array.isArray(patient.img)) {
                      imagens = patient.img.map((imgObj) => imgObj.imagem);
                    } else if (typeof patient.img === "string") {
                      const cleanedString = patient.img
                        .replace(/^\["|"\]$/g, "")
                        .replace(/"/g, "");
                      imagens = cleanedString
                        .split(",")
                        .map((img) => img.trim())
                        .filter((img) => img !== "");
                    }

                    return (
                      <React.Fragment key={patient.id}>
                        {imagens.length > 0 ? (
                          imagens.map((imgSrc, index) => (
                            <div
                              key={index}
                              className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition"
                            >
                              <div className="relative group">
                                <img
                                  src={`data:image/png;base64,${imgSrc}`}
                                  alt={`Documento ${index + 1}`}
                                  className="w-full h-32 md:h-48 object-contain cursor-pointer bg-gray-100"
                                  onClick={() => handleImageClick(imgSrc)}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition flex items-center justify-center">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6 md:h-8 md:w-8 text-white opacity-0 group-hover:opacity-100 transition"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3-3H7"
                                    />
                                  </svg>
                                </div>
                              </div>
                              <div className="p-2 bg-gray-50 text-center border-t">
                                <span className="text-xs md:text-sm text-gray-800">
                                  Documento {index + 1}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="col-span-2 text-center py-6 md:py-8">
                            <DocumentTextIcon className="h-8 w-8 md:h-12 md:w-12 mx-auto text-gray-400 mb-2 md:mb-3" />
                            <p className="text-sm md:text-base text-gray-500">
                              Nenhum documento encontrado para este paciente.
                            </p>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
              </div>

              <div className="mt-auto pt-3 md:pt-4 border-t">
                <button
                  onClick={closeFotosCurativo}
                  className="w-full bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 transition text-sm md:text-base"
                >
                  Fechar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para imagem ampliada */}
      <AnimatePresence>
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              className="relative max-w-4xl max-h-full p-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <img
                src={`data:image/png;base64,${selectedImage}`}
                alt="Imagem ampliada"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              <button
                onClick={closeModal}
                className="absolute top-2 right-2 md:top-4 md:right-4 bg-red-600 text-white rounded-full p-1 md:p-2 hover:bg-red-700 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 md:h-6 md:w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;